import { useState } from "react";
import { X, RotateCcw, Search } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/input";

interface FormTemplate {
  id: string;
  title: string;
  description: string;
  status?: string;
}

interface ArchiveModalProps {
  isOpen: boolean;
  archivedTemplates: FormTemplate[];
  onClose: () => void;
  onUnarchive: (id: string) => void;
}

export default function ArchiveModal({
  isOpen,
  archivedTemplates,
  onClose,
  onUnarchive,
}: ArchiveModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); 

  if (!isOpen) return null;

  const filtered = archivedTemplates.filter(
    (t) =>
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase())
  );


  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTemplates = filtered.slice(startIndex, startIndex + itemsPerPage);

  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-3xl p-6 relative">
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          onClick={onClose}
        >
          <X size={20} />
        </button>

        <h2 className="text-xl font-semibold text-[#402B69] mb-4">
          Archived Templates
        </h2>

        <div className="relative mb-4">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            placeholder="Search archived templates..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10"
          />
        </div>

        {paginatedTemplates.length > 0 ? (
          <>
            <div className="max-h-80 overflow-y-auto border-t border-gray-200 mt-2">
              <table className="w-full text-sm">
                <thead className="bg-[#f4f2fb] sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-[#402B69]">Title</th>
                    <th className="px-4 py-2 text-left text-[#402B69]">Description</th>
                    <th className="px-4 py-2 text-[#402B69]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTemplates.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 max-w-[100px] overflow-hidden whitespace-nowrap text-ellipsis">{t.title}</td>
<td className="px-4 py-2 text-gray-600 max-w-[200px] overflow-hidden whitespace-nowrap text-ellipsis">
  {t.description}
</td>                      <td className="px-4 py-2 flex items-center justify-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onUnarchive(t.id)}
                          className="flex items-center gap-1 hover:bg-[#eef0ff]"
                        >
                          <RotateCcw size={14} /> Unarchive
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
                <div>
                  Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filtered.length)} of{" "}
                  {filtered.length} templates
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={handlePrevPage}
                  >
                    Previous
                  </Button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={handleNextPage}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-500 text-center mt-6">No archived templates found.</p>
        )}
      </div>
    </div>
  );
}
